import { action, makeObservable, observable } from 'mobx';
import { useObserver } from 'mobx-react-lite';
import { useCallback, useEffect } from 'react';

import type { ListKey } from './useList.keys';

export interface IUseListOptions {
  /**
   * Initial list of items
   */
  initialItems?: Array<string | number>;
}

export interface IUseListReturn {
  /**
   * Add a single item or array of items to the list
   */
  add: (item: string | number | Array<string | number>) => void;

  /**
   * Remove a single item or array of items from the list
   */
  remove: (item: string | number | Array<string | number>) => void;

  /**
   * Reset the list to empty
   */
  reset: () => void;

  /**
   * Set the entire list to the provided items
   */
  set: (items: Array<string | number>) => void;

  /**
   * Get the current list items
   */
  get: () => Array<string | number>;

  /**
   * Check if the list contains a specific item
   */
  has: (item: string | number) => boolean;

  /**
   * Whether the list is empty
   */
  isEmpty: boolean;

  /**
   * Number of items in the list
   */
  count: number;
}

class SelectionStore {
  selections = observable.map<string, Set<string | number>>();

  constructor() {
    makeObservable(this, {
      selections: observable,
      add: action,
      remove: action,
      reset: action,
      set: action,
    });
  }

  add = (key: string, items: Array<string | number>): void => {
    const selection = this.selections.get(key) || new Set();
    items.forEach((item) => selection.add(item));
    this.selections.set(key, selection);
  };

  remove = (key: string, items: Array<string | number>): void => {
    const selection = this.selections.get(key);
    if (selection) {
      items.forEach((item) => selection.delete(item));
    }
  };

  reset = (key: string): void => {
    this.selections.set(key, new Set());
  };

  set = (key: string, items: Array<string | number>): void => {
    this.selections.set(key, new Set(items));
  };

  get = (key: string): Array<string | number> => {
    const selection = this.selections.get(key) || new Set();
    return Array.from(selection);
  };

  has = (key: string, item: string | number): boolean => {
    const selection = this.selections.get(key);
    return selection ? selection.has(item) : false;
  };

  getCount = (key: string): number => {
    const selection = this.selections.get(key);
    return selection ? selection.size : 0;
  };

  isEmpty = (key: string): boolean => {
    return this.getCount(key) === 0;
  };
}

// Global MobX store instance
const globalSelectionStore = new SelectionStore();

// Export for testing
export const TESTING_EXPORTS = {
  globalSelectionStore,
};

/**
 * Hook for managing dynamic lists of items with keyed selections.
 * Supports add, remove, reset, has, isEmpty, and count operations.
 * Can maintain separate lists using a unique key.
 *
 * **Important**: Components using this hook must be wrapped with `observer()`
 * from mobx-react-lite for reactivity to work properly.
 *
 * @param key - Registered list key from LIST_KEYS (e.g., SELECTED_USERS, SELECTED_PERSONS)
 * @param options - Configuration options
 * @param options.initialItems - Initial items to populate the list
 * @returns Object with list management methods and computed properties
 *
 * @example
 * ```typescript
 * import { observer } from 'mobx-react-lite';
 * import { useList, SELECTED_USERS, SELECTED_PERSONS } from '@feathr/hooks';
 *
 * const MyComponent = observer(() => {
 *   const usersList = useList(SELECTED_USERS);
 *   const personsList = useList(SELECTED_PERSONS, { initialItems: [1, 2] });
 *
 *   const handleAdd = () => {
 *     usersList.add([1, 2, 3]);
 *     personsList.add('person_123');
 *   };
 *
 *   return (
 *     <div>
 *       <p>Users: {usersList.count}</p>
 *       <p>Persons: {personsList.count}</p>
 *       <p>Users empty: {usersList.isEmpty}</p>
 *       <button onClick={handleAdd}>Add Items</button>
 *     </div>
 *   );
 * });
 * ```
 */
export function useList(key: ListKey, options: IUseListOptions = {}): IUseListReturn {
  const { initialItems = [] } = options;

  // Initialize selection if it doesn't exist
  useEffect(() => {
    if (!globalSelectionStore.selections.has(key)) {
      globalSelectionStore.set(key, initialItems);
    }
  }, [key, initialItems]);

  const add = useCallback(
    (item: string | number | Array<string | number>) => {
      const items = Array.isArray(item) ? item : [item];
      globalSelectionStore.add(key, items);
    },
    [key],
  );

  const remove = useCallback(
    (item: string | number | Array<string | number>) => {
      const items = Array.isArray(item) ? item : [item];
      globalSelectionStore.remove(key, items);
    },
    [key],
  );

  const reset = useCallback(() => {
    globalSelectionStore.reset(key);
  }, [key]);

  const set = useCallback(
    (items: Array<string | number>) => {
      globalSelectionStore.set(key, items);
    },
    [key],
  );

  const get = useCallback(() => {
    return globalSelectionStore.get(key);
  }, [key]);

  const has = useCallback(
    (item: string | number) => {
      return globalSelectionStore.has(key, item);
    },
    [key],
  );

  // Using useObserver to ensure reactivity works reliably
  // Note: While useObserver is deprecated, it's the most reliable way to make hooks reactive
  return useObserver(() => ({
    add,
    remove,
    reset,
    set,
    get,
    has,
    isEmpty: globalSelectionStore.isEmpty(key),
    count: globalSelectionStore.getCount(key),
  }));
}
